// Astrology Transit Calculator
//
// Calculates ecliptic longitudes for celestial bodies, Ascendant, Midheaven,
// and Equal House cusps for a given date/time and geographic location.
//
// Uses astronomy-engine for precise planetary positions (VSOP87 model).

import {
  Body,
  type FlexibleDateTime,
  Ecliptic as AstroEcliptic,
  GeoVector,
  MakeTime,
  SunPosition,
} from 'astronomy-engine'

// ─── Types ───

export type PlanetName =
  | 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars'
  | 'Jupiter' | 'Saturn' | 'Uranus' | 'Neptune' | 'Pluto'

export interface PlanetPosition {
  name: PlanetName
  longitude: number    // 0–360 ecliptic longitude
  signIndex: number    // 0–11 (Aries=0)
  degree: number       // whole degrees within sign (0–29)
  minute: number       // arc minutes within degree
}

export interface HouseCusp {
  house: number        // 1–12
  longitude: number    // ecliptic longitude of cusp
}

export interface TransitChart {
  date: Date
  latitude: number
  longitude: number
  ascendant: number
  midheaven: number
  planets: PlanetPosition[]
  houses: HouseCusp[]
}

// ─── Constants ───

const PLANETS: readonly PlanetName[] = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto',
]

const BODY_MAP: Partial<Record<PlanetName, (typeof Body)[keyof typeof Body]>> = {
  Moon: Body.Moon,
  Mercury: Body.Mercury,
  Venus: Body.Venus,
  Mars: Body.Mars,
  Jupiter: Body.Jupiter,
  Saturn: Body.Saturn,
  Uranus: Body.Uranus,
  Neptune: Body.Neptune,
  Pluto: Body.Pluto,
}

export const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
] as const

export const SIGN_SYMBOLS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'] as const

// ─── Planet Positions ───

function getEclipticLongitude(body: PlanetName, date: Date): number {
  const t = MakeTime(date as unknown as FlexibleDateTime)
  if (body === 'Sun') return SunPosition(t).elon
  const astroBody = BODY_MAP[body]
  if (!astroBody) throw new Error(`Unknown body: ${body}`)
  const geo = GeoVector(astroBody, t, true)
  return AstroEcliptic(geo).elon
}

function lonToPosition(name: PlanetName, lon: number): PlanetPosition {
  const normalized = ((lon % 360) + 360) % 360
  const signIndex = Math.floor(normalized / 30)
  const withinSign = normalized - signIndex * 30
  const degree = Math.floor(withinSign)
  const minute = Math.round((withinSign - degree) * 60)
  return { name, longitude: normalized, signIndex, degree, minute }
}

// ─── Ascendant & Midheaven ───

function dateToJulianDay(date: Date): number {
  return date.getTime() / 86400000 + 2440587.5
}

function calculateGMST(jd: number): number {
  const T = (jd - 2451545.0) / 36525.0
  let gmst = 280.46061837
    + 360.98564736629 * (jd - 2451545.0)
    + 0.000387933 * T * T
    - T * T * T / 38710000.0
  return ((gmst % 360) + 360) % 360
}

function obliquity(jd: number): number {
  const T = (jd - 2451545.0) / 36525.0
  return 23.439291 - 0.0130042 * T - 1.64e-7 * T * T + 5.04e-7 * T * T * T
}

function calculateASC(ramcDeg: number, latDeg: number, oblDeg: number): number {
  const ramc = ramcDeg * Math.PI / 180
  const lat = latDeg * Math.PI / 180
  const obl = oblDeg * Math.PI / 180

  const y = -Math.cos(ramc)
  const x = Math.sin(obl) * Math.tan(lat) + Math.cos(obl) * Math.sin(ramc)
  let asc = Math.atan2(y, x) * 180 / Math.PI
  return ((asc % 360) + 360) % 360
}

function calculateMC(ramcDeg: number, oblDeg: number): number {
  const ramc = ramcDeg * Math.PI / 180
  const obl = oblDeg * Math.PI / 180
  let mc = Math.atan2(Math.sin(ramc), Math.cos(ramc) * Math.cos(obl)) * 180 / Math.PI
  return ((mc % 360) + 360) % 360
}

// ─── Houses (Equal House from ASC) ───

function calculateEqualHouses(asc: number): HouseCusp[] {
  return Array.from({ length: 12 }, (_, i) => ({
    house: i + 1,
    longitude: ((asc - i * 30) % 360 + 360) % 360,
  }))
}

// ─── Main ───

export function calculateTransitChart(
  date: Date,
  latitude: number,
  geoLongitude: number,
): TransitChart {
  const jd = dateToJulianDay(date)
  const gmst = calculateGMST(jd)
  const lst = ((gmst + geoLongitude) % 360 + 360) % 360
  const obl = obliquity(jd)

  const ascendant = calculateASC(lst, latitude, obl)
  const midheaven = calculateMC(lst, obl)

  const planets = PLANETS.map(name => {
    const lon = getEclipticLongitude(name, date)
    return lonToPosition(name, lon)
  })

  const houses = calculateEqualHouses(ascendant)

  return { date, latitude, longitude: geoLongitude, ascendant, midheaven, planets, houses }
}

// ─── Helpers (exported for chart SVG) ───

export function lonToDegMin(lon: number): { sign: number; deg: number; min: number } {
  const n = ((lon % 360) + 360) % 360
  const sign = Math.floor(n / 30)
  const within = n - sign * 30
  return { sign, deg: Math.floor(within), min: Math.round((within - Math.floor(within)) * 60) }
}
